import { useCallback, useContext, useEffect, useRef } from "react";

import { useSessionStorage } from "@react-hooks-library/core";
import { deleteUserKv, putUserKv } from "@rubi/api-generated";

import { AuthStateContext } from "@/contexts/AuthContext";

/** Validates one persistent filter field's current (non-null) value against live option data. */
type FilterValidator<V> = (value: V) => boolean;

export type PersistentFilterValidators<T> = {
  [K in keyof T]?: FilterValidator<NonNullable<T[K]>>;
};

interface UsePersistentFiltersConfig<T extends object> {
  storageKey: string;
  /**
   * Current org id, used to namespace the saved and session keys so a staff user switching
   * orgs (e.g. via PartnerOrgSwitcher) doesn't inherit another org's filters.
   * Persistence is skipped (filters stay session-only) while it is null/undefined.
   */
  orgId: string | null | undefined;
  defaultValue: T;
  /**
   * Allowlisted fields that are saved to the user's account and restored across
   * login sessions; every other field stays session-only. Explicit so adding or
   * removing a filter never silently changes persistence behaviour.
   */
  persistentKeys: readonly (keyof T)[];
}

const SAVE_DEBOUNCE_MS = 500;

const pickPersistent = <T extends object>(
  value: Partial<T>,
  persistentKeys: readonly (keyof T)[],
): Partial<T> => {
  const picked: Partial<T> = {};
  for (const field of persistentKeys) {
    if (field in value) picked[field] = value[field];
  }
  return picked;
};

/**
 * Drop-in replacement for `useSessionStorage<T>` filter state that additionally
 * persists an allowlisted subset of fields to the user's account across login
 * sessions (rubi-frontend#222). Saved values arrive with the current user from
 * `/user/me` (`user_kv`), so the first render already shows them; changes are
 * written back via `PUT /user_kv/{key}`, keyed by `storageKey` and `orgId`.
 * Non-allowlisted fields (date ranges, search text, pagination, image search,
 * ...) keep the prior session-only behaviour.
 *
 * Returns `[filters, setFilters, validateFilters]`. Call `validateFilters` once live
 * option data (statuses, report types, users, ...) is available; any persistent field
 * whose value is no longer valid is reset to its default immediately, in both state
 * and the saved value, rather than left sitting around stale.
 */
export const usePersistentFilters = <T extends object>({
  storageKey,
  orgId,
  defaultValue,
  persistentKeys,
}: UsePersistentFiltersConfig<T>): [
  T,
  (value: T) => void,
  (validators: PersistentFilterValidators<T>) => void,
] => {
  const { currentUser } = useContext(AuthStateContext);
  const savedKey = orgId ? `${storageKey}:${orgId}` : null;
  const sessionKey = savedKey ?? storageKey;

  const initialFiltersFor = (key: string | null): T => {
    const saved = key ? currentUser?.user_kv?.[key] : undefined;
    return {
      ...defaultValue,
      ...(saved ? pickPersistent(saved as Partial<T>, persistentKeys) : {}),
    };
  };

  /*
   * useSessionStorage takes its default eagerly (no lazy-initializer form), so
   * computing it inline here would re-run on every render. A ref caches it to
   * run once per mount instead. Safe because consumers only mount this hook
   * once currentUser is already resolved (behind ProtectedPage).
   */
  const initialFiltersRef = useRef<T>();
  if (initialFiltersRef.current === undefined) {
    initialFiltersRef.current = initialFiltersFor(savedKey);
  }
  const [filters, setSessionFilters] = useSessionStorage<T>(
    sessionKey,
    initialFiltersRef.current,
  );

  // Session-only changes (e.g. typing a tracking number) leave this unchanged, so they skip the write.
  const lastSavedRef = useRef(
    JSON.stringify(pickPersistent(initialFiltersRef.current, persistentKeys)),
  );
  const pendingSaveRef = useRef<(() => void) | null>(null);
  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const flushSave = useCallback(() => {
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    saveTimeoutRef.current = null;
    pendingSaveRef.current?.();
    pendingSaveRef.current = null;
  }, []);

  useEffect(() => flushSave, [flushSave]);

  /*
   * Switching orgs only changes a route param, so consumers don't remount and
   * useSessionStorage keeps the previous org's state. Load the new org's
   * filters explicitly: this session's copy if there is one, else the saved value.
   */
  const sessionKeyRef = useRef(sessionKey);
  useEffect(() => {
    if (sessionKeyRef.current === sessionKey) return;
    sessionKeyRef.current = sessionKey;
    flushSave();
    const stored = sessionStorage.getItem(sessionKey);
    const next = stored
      ? (JSON.parse(stored) as T)
      : initialFiltersFor(savedKey);
    lastSavedRef.current = JSON.stringify(pickPersistent(next, persistentKeys));
    setSessionFilters(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionKey]);

  const setFilters = useCallback(
    (value: T) => {
      setSessionFilters(value);
      if (!savedKey) return;

      const toSave = pickPersistent(value, persistentKeys);
      const serialized = JSON.stringify(toSave);
      if (serialized === lastSavedRef.current) return;
      lastSavedRef.current = serialized;
      const isDefault =
        serialized ===
        JSON.stringify(pickPersistent(defaultValue, persistentKeys));
      pendingSaveRef.current = () =>
        isDefault
          ? void deleteUserKv({ path: { key: savedKey } })
          : void putUserKv({
              path: { key: savedKey },
              body: { value: toSave },
            });
      if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
      saveTimeoutRef.current = setTimeout(flushSave, SAVE_DEBOUNCE_MS);
    },
    [setSessionFilters, savedKey, persistentKeys, defaultValue, flushSave],
  );

  const validateFilters = useCallback(
    (validators: PersistentFilterValidators<T>) => {
      let changed = false;
      const next = { ...filters };
      for (const key of persistentKeys) {
        const validator = validators[key];
        const value = filters[key];
        if (!validator || value === null || value === undefined) continue;
        if (!validator(value as NonNullable<T[typeof key]>)) {
          next[key] = defaultValue[key];
          changed = true;
        }
      }
      if (changed) setFilters(next);
    },
    [filters, persistentKeys, defaultValue, setFilters],
  );

  return [filters, setFilters, validateFilters];
};
